import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import SectionHeading from "@/components/SectionHeading";
import { USE_CASES } from "@/data/content";

const TAGS = ["All", "Odoo ERP", "Cyber Security", "Custom Software"];

export default function UseCases() {
  const [active, setActive] = useState("All");

  const filtered = useMemo(() => {
    if (active === "All") return USE_CASES;
    return USE_CASES.filter((c) => c.tags.includes(active));
  }, [active]);

  return (
    <div data-testid="use-cases-page" className="bg-white">
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 border-b border-slate-200">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <div className="overline mb-5">Use cases</div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.02] font-black text-slate-900">
              Real systems, real outcomes, <span className="text-[#1D4ED8]">in production.</span>
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-base md:text-lg text-slate-600 leading-relaxed font-light">
              From a Class-1 government civil contractor to charities in the Middle East — a curated set of engagements that show how we deliver.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap gap-2 mb-12">
            {TAGS.map((t) => (
              <button
                key={t}
                data-testid={`filter-${t.toLowerCase().replace(/ /g, '-')}`}
                onClick={() => setActive(t)}
                className={`px-4 py-2 font-mono text-[11px] tracking-[0.22em] uppercase border transition-colors ${
                  active === t
                    ? "bg-[#1D4ED8] border-[#1D4ED8] text-white"
                    : "bg-white border-slate-300 text-slate-600 hover:border-[#1D4ED8] hover:text-[#1D4ED8]"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {filtered.map((c, i) => (
              <motion.article
                key={c.client}
                data-testid={`use-case-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
                className="card-flat p-7 md:p-9 flex flex-col"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  {c.tags.map((t) => (
                    <span
                      key={t}
                      className="px-2.5 py-1 font-mono text-[10px] tracking-[0.18em] uppercase border border-slate-300 text-slate-700 bg-white"
                    >
                      {t}
                    </span>
                  ))}
                </div>
                <h3 className="mt-5 font-display text-2xl md:text-3xl font-bold text-slate-900 tracking-tight leading-tight">
                  {c.client}
                </h3>
                <div className="mt-1 font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500">
                  {c.sector}
                </div>

                <div className="mt-6 space-y-5">
                  <div>
                    <div className="overline mb-2">Challenge</div>
                    <p className="text-sm text-slate-700 leading-relaxed">{c.challenge}</p>
                  </div>
                  <div>
                    <div className="overline mb-2">Solution</div>
                    <p className="text-sm text-slate-700 leading-relaxed">{c.solution}</p>
                  </div>
                  <div>
                    <div className="overline mb-2">Impact</div>
                    <p className="text-sm text-slate-700 leading-relaxed">{c.impact}</p>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
